// ========================================
// Master Calendar View
// ========================================

// Calendario general, diseño C (2026-10-26): arriba la semana (cuántas clases tiene cada día y de qué
// formaciones), debajo el día elegido con una columna por formación; «Ver mes» abre el mes completo.
// Estilos: assets/css/views/calendar.css (.cg-*).
const CalendarView = {
    calendar: null,
    mode: 'day', // 'day' | 'month'
    selectedDate: null,
    HOUR_PX: 48,

    // Colours with white text that stay apart from each other; one per formation, in list order.
    PALETTE: ['#0E6B62', '#C2410C', '#7C3AED', '#2563EB', '#BE185D', '#0E7490', '#4D7C0F', '#92400E', '#475569', '#B91C1C', '#4338CA', '#15803D'],

    render() {
        if (!this.selectedDate) this.selectedDate = this.startOfDay(new Date());
        const month = this.mode === 'month';
        return `
            <div class="dnr-proposal cg-view">
                <header class="cg-head">
                    <h1 class="cg-title">Calendario general</h1>
                    <select class="cg-select" id="filter-formation" aria-label="Formación" ${UIEvents.attr("change", function(event,captured){CalendarView.applyFilters();}, [])}>
                        <option value="">Todas las formaciones</option>
                        ${this.renderFormationOptions()}
                    </select>
                    <select class="cg-select" id="filter-professor" aria-label="Profesor" ${UIEvents.attr("change", function(event,captured){CalendarView.applyFilters();}, [])}>
                        <option value="">Todos los profesores</option>
                        ${this.renderProfessorOptions()}
                    </select>
                    <input type="date" class="cg-select" id="cg-goto" aria-label="Ir a una fecha" value="${this.dateKey(this.selectedDate)}" ${UIEvents.attr("change", function(event,captured){CalendarView.gotoDate(this.value);}, [])}>
                    <button type="button" class="cg-btn ${month ? 'is-on' : ''}" id="cg-mode" aria-pressed="${month}" ${UIEvents.attr("click", function(event,captured){CalendarView.toggleMonth();}, [])}>
                        ${month ? '<i class="fas fa-calendar-day"></i> Ver día' : '<i class="fas fa-calendar"></i> Ver mes'}
                    </button>
                </header>
                ${month ? `
                <section class="cg-card cg-month"><div id="main-calendar"></div></section>` : `
                <section class="cg-week" id="cg-week" aria-label="Semana"></section>
                <section class="cg-card cg-day" id="cg-day"></section>`}
            </div>
        `;
    },

    renderProfessorOptions() {
        const professors = DataManager.getProfessors();
        const chosen = String(this.filterState?.professorId || '');
        return professors.map(prof => `
            <option value="${Utils.sanitizeHTML(prof.id)}" ${String(prof.id) === chosen ? 'selected' : ''}>${Utils.sanitizeHTML(prof.firstName)} ${Utils.sanitizeHTML(prof.lastName)}</option>
        `).join('');
    },

    renderFormationOptions() {
        const formations = DataManager.getFormations();
        const chosen = String(this.filterState?.formationId || '');
        return formations.map(formation => `
            <option value="${Utils.sanitizeHTML(formation.id)}" ${String(formation.id) === chosen ? 'selected' : ''}>${Utils.sanitizeHTML(formation.name)}</option>
        `).join('');
    },

    // --- dates ---
    startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; },
    addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; },
    dateKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; },
    weekStart(d) { return this.addDays(this.startOfDay(d), -((d.getDay() + 6) % 7)); },
    minutes(t) { const [h, m] = String(t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); },

    // The chosen filters, kept across re-renders (switching between day and month).
    filters() {
        const professor = document.getElementById('filter-professor'), formation = document.getElementById('filter-formation');
        if (professor && formation) this.filterState = { professorId: professor.value || '', formationId: formation.value || '' };
        return { professorId: '', formationId: '', ...this.filterState };
    },

    // Every class session that falls on [from, to] (inclusive dates), one per real date. A class is a
    // single date (startDate) or a weekly slot between startDate and endDate on its dayOfWeek.
    occurrences(from, to, filters = {}) {
        const fromKey = this.dateKey(from), toKey = this.dateKey(to);
        const out = [];
        DataManager.getClasses().forEach(cls => {
            if (!cls.startDate || !cls.startTime || !cls.endTime) return;
            if (filters.formationId && String(cls.formationId) !== String(filters.formationId)) return;
            const subject = DataManager.getSubject(cls.subjectId);
            const professorId = cls.professorId || subject?.professorId;
            if (filters.professorId && String(professorId) !== String(filters.professorId)) return;
            const start = cls.startDate, end = cls.endDate && cls.endDate > cls.startDate ? cls.endDate : cls.startDate;
            if (start > toKey || end < fromKey) return;
            if (start === end) { out.push({ cls, subject, professorId, date: start }); return; }
            for (let d = new Date(`${start > fromKey ? start : fromKey}T12:00:00`); this.dateKey(d) <= (end < toKey ? end : toKey); d = this.addDays(d, 1)) {
                if (d.getDay() === Number(cls.dayOfWeek)) out.push({ cls, subject, professorId, date: this.dateKey(d) });
            }
        });
        return out;
    },

    // --- week strip ---
    renderWeek() {
        const box = document.getElementById('cg-week');
        if (!box) return;
        const monday = this.weekStart(this.selectedDate);
        const sunday = this.addDays(monday, 6);
        const list = this.occurrences(monday, sunday, this.filters());
        const today = this.dateKey(new Date()), selected = this.dateKey(this.selectedDate);
        const days = Array.from({ length: 7 }, (_, i) => this.addDays(monday, i));
        SafeHTML.set(box, `
            <button type="button" class="cg-week-nav" aria-label="Semana anterior" ${UIEvents.attr("click", function(event,captured){CalendarView.moveWeek(-1);}, [])}><i class="fas fa-chevron-left"></i></button>
            ${days.map(d => {
                const key = this.dateKey(d);
                const dayList = list.filter(o => o.date === key);
                const colours = [...new Set(dayList.map(o => String(o.cls.formationId)))].map(id => this.getColorForFormation(id));
                const label = d.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
                return `
                    <button type="button" class="cg-week-day ${key === selected ? 'is-on' : ''} ${key === today ? 'is-today' : ''} ${[0, 6].includes(d.getDay()) ? 'is-weekend' : ''}"
                        aria-pressed="${key === selected}" aria-label="${Utils.sanitizeHTML(d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }))}: ${dayList.length} clases"
                        ${UIEvents.attr("click", function(event,captured){CalendarView.gotoDate(String(captured[0]));}, [key])}>
                        <span class="cg-week-dow">${Utils.sanitizeHTML(label)}</span>
                        <span class="cg-week-n">${d.getDate()}</span>
                        <span class="cg-week-count">${dayList.length} ${dayList.length === 1 ? 'clase' : 'clases'}</span>
                        <span class="cg-week-dots" aria-hidden="true">${colours.slice(0, 12).map(c => `<i style="background: ${c};"></i>`).join('')}</span>
                    </button>`;
            }).join('')}
            <button type="button" class="cg-week-nav" aria-label="Semana siguiente" ${UIEvents.attr("click", function(event,captured){CalendarView.moveWeek(1);}, [])}><i class="fas fa-chevron-right"></i></button>
        `);
    },

    // --- the chosen day, one column per formation ---
    renderDay() {
        const box = document.getElementById('cg-day');
        if (!box) return;
        const filters = this.filters();
        const key = this.dateKey(this.selectedDate);
        const list = this.occurrences(this.selectedDate, this.selectedDate, filters);
        const formations = DataManager.getFormations();
        const ids = filters.formationId ? [String(filters.formationId)] : [...new Set(list.map(o => String(o.cls.formationId)))];
        const columns = formations.filter(f => ids.includes(String(f.id)));
        const title = this.selectedDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        const header = `
            <div class="cg-day-head">
                <h2 class="cg-h2">${Utils.sanitizeHTML(title.charAt(0).toUpperCase() + title.slice(1))}</h2>
                <span class="cg-hint">${list.length ? `${list.length} ${list.length === 1 ? 'clase' : 'clases'} · pulsa un hueco para programar` : ''}</span>
                <button type="button" class="cg-btn is-primary" ${UIEvents.attr("click", function(event,captured){CalendarView.scheduleAt(String(captured[0]),'09:00','');}, [key])}><i class="fas fa-plus"></i> Programar clase</button>
            </div>`;
        if (!columns.length) {
            SafeHTML.set(box, header + '<p class="cg-empty">No hay clases este día.</p>');
            return;
        }
        let from = 8 * 60, to = 20 * 60;
        list.forEach(o => { from = Math.min(from, this.minutes(o.cls.startTime)); to = Math.max(to, this.minutes(o.cls.endTime)); });
        from = Math.floor(from / 60) * 60; to = Math.ceil(to / 60) * 60;
        const px = this.HOUR_PX / 60;
        const hours = [];
        for (let m = from; m < to; m += 60) hours.push(`${String(m / 60).padStart(2, '0')}:00`);
        const column = formation => {
            const items = list.filter(o => String(o.cls.formationId) === String(formation.id))
                .sort((a, b) => this.minutes(a.cls.startTime) - this.minutes(b.cls.startTime));
            // Classes that overlap in one formation share the column side by side.
            const lanes = [];
            items.forEach(o => {
                const s = this.minutes(o.cls.startTime);
                let lane = lanes.findIndex(end => end <= s);
                if (lane < 0) { lane = lanes.length; lanes.push(0); }
                lanes[lane] = this.minutes(o.cls.endTime);
                o.lane = lane;
            });
            const width = 100 / Math.max(1, lanes.length);
            const colour = this.getColorForFormation(formation.id);
            return `
                <div class="cg-col" data-formation-id="${Utils.sanitizeHTML(formation.id)}" style="height: ${(to - from) * px}px;"
                    ${UIEvents.attr("click", function(event,captured){CalendarView.handleColumnClick(event,this,String(captured[0]),String(captured[1]),Number(captured[2]));}, [formation.id, key, from])}>
                    ${items.map(o => {
                        const professor = o.professorId ? DataManager.getProfessor(o.professorId) : null;
                        const top = (this.minutes(o.cls.startTime) - from) * px;
                        const height = Math.max(22, (this.minutes(o.cls.endTime) - this.minutes(o.cls.startTime)) * px - 3);
                        return `
                            <button type="button" class="cg-event" data-class-id="${Utils.sanitizeHTML(o.cls.id)}"
                                style="top: ${top + 1}px; height: ${height}px; left: calc(${o.lane * width}% + 3px); width: calc(${width}% - 6px); background: ${colour};"
                                ${UIEvents.attr("click", function(event,captured){event.stopPropagation();CalendarView.showEventDetailsById(String(captured[0]));}, [o.cls.id])}>
                                <span class="cg-event-time">${Utils.sanitizeHTML(o.cls.startTime)}–${Utils.sanitizeHTML(o.cls.endTime)}</span>
                                <b class="cg-event-name">${Utils.sanitizeHTML(o.subject ? o.subject.name : 'Clase')}</b>
                                <span class="cg-event-meta">${Utils.sanitizeHTML(professor ? `${professor.firstName} ${professor.lastName}` : 'Sin profesor')}</span>
                                ${o.cls.classroom ? `<span class="cg-event-meta">${Utils.sanitizeHTML(o.cls.classroom)}</span>` : ''}
                            </button>`;
                    }).join('')}
                </div>`;
        };
        SafeHTML.set(box, `
            ${header}
            <div class="cg-grid-wrap">
                <div class="cg-grid" style="grid-template-columns: 52px repeat(${columns.length}, minmax(140px, 1fr)); min-width: ${52 + columns.length * 140}px;">
                    <span class="cg-corner"></span>
                    ${columns.map(f => `<span class="cg-col-head"><i style="background: ${this.getColorForFormation(f.id)};"></i>${Utils.sanitizeHTML(f.name)}</span>`).join('')}
                    <div class="cg-hours">${hours.map(h => `<span style="height: ${this.HOUR_PX}px;">${h}</span>`).join('')}</div>
                    ${columns.map(column).join('')}
                </div>
            </div>
        `);
    },

    handleColumnClick(event, column, formationId, dateKey, from) {
        if (event.target.closest('.cg-event')) return;
        const y = event.clientY - column.getBoundingClientRect().top;
        const minutes = from + Math.max(0, Math.floor(y / (this.HOUR_PX / 2)) * 30);
        const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
        this.scheduleAt(dateKey, time, formationId);
    },

    scheduleAt(dateKey, time, formationId) {
        const date = new Date(`${dateKey}T${time}:00`);
        this.showQuickScheduleForm({ date }, formationId);
    },

    showEventDetailsById(classId) {
        const cls = DataManager.getClasses().find(c => String(c.id) === String(classId));
        if (cls) this.showEventDetails(cls);
    },

    moveWeek(delta) {
        this.selectedDate = this.addDays(this.selectedDate, delta * 7);
        this.refresh();
    },

    gotoDate(dateStr) {
        if (!dateStr) return;
        this.selectedDate = this.startOfDay(new Date(`${dateStr}T12:00:00`));
        if (this.mode !== 'day') { this.mode = 'day'; App.render(); return; }
        this.refresh();
    },

    toggleMonth() {
        this.mode = this.mode === 'month' ? 'day' : 'month';
        App.render();
    },

    // Loads the classes of what is on screen, then draws it.
    refresh() {
        const goto = document.getElementById('cg-goto');
        if (goto) goto.value = this.dateKey(this.selectedDate);
        if (this.mode === 'month') { this.reloadMonthEvents(); return; }
        const monday = this.weekStart(this.selectedDate);
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            FileStorage.needClasses(monday, this.addDays(monday, 6), () => { this.renderWeek(); this.renderDay(); });
        }
        this.renderWeek();
        this.renderDay();
    },

    init() {
        if (!this.selectedDate) this.selectedDate = this.startOfDay(new Date());
        this.calendar = null;
        if (this.mode === 'month') setTimeout(() => this.initCalendar(), 0);
        else this.refresh();
    },

    initCalendar() {
        const calendarEl = document.getElementById('main-calendar');
        if (!calendarEl || typeof FullCalendar === 'undefined') return;
        this.calendar = new FullCalendar.Calendar(calendarEl, {
            initialView: 'dayGridMonth',
            initialDate: this.selectedDate,
            headerToolbar: { left: 'prev,next today', center: 'title', right: '' },
            locale: 'es',
            firstDay: 1,
            buttonIcons: false,
            buttonText: { prev: '‹', next: '›' },
            height: 'auto',
            dayMaxEvents: 4,
            events: [],
            dateClick: info => this.gotoDate(info.dateStr),
            eventClick: info => this.showEventDetails(info.event.extendedProps.classData),
            datesSet: info => {
                if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
                    FileStorage.needClasses(info.start, new Date(info.end.getTime() - 1), () => this.reloadMonthEvents());
                }
                this.reloadMonthEvents();
            }
        });
        this.calendar.render();
    },

    reloadMonthEvents() {
        if (!this.calendar) return;
        const view = this.calendar.view;
        this.calendar.removeAllEvents();
        this.generateCalendarEvents(this.filters(), view.activeStart, this.addDays(view.activeEnd, -1)).forEach(e => this.calendar.addEvent(e));
    },

    generateCalendarEvents(filters = {}, from = this.weekStart(this.selectedDate || new Date()), to = this.addDays(from, 6)) {
        return this.occurrences(from, to, filters).map(o => {
            const colour = this.getColorForFormation(o.cls.formationId);
            return {
                title: o.subject ? o.subject.name : 'Clase',
                start: `${o.date}T${o.cls.startTime}`,
                end: `${o.date}T${o.cls.endTime}`,
                backgroundColor: colour,
                borderColor: colour,
                extendedProps: { classData: o.cls }
            };
        });
    },

    getColorForFormation(formationId) {
        const index = DataManager.getFormations().findIndex(f => String(f.id) === String(formationId));
        return index < 0 ? '#64748b' : this.PALETTE[index % this.PALETTE.length];
    },

    applyFilters() {
        this.refresh();
    },

    showEventDetails(classData) {
        const subject = DataManager.getSubject(classData.subjectId);
        const formation = DataManager.getFormation(classData.formationId);
        const professor = DataManager.getProfessor(classData.professorId);
        const duration = Utils.calculateDuration(classData.startTime, classData.endTime);
        const color = this.getColorForFormation(classData.formationId);

        const content = `
            <div class="dnr-proposal cal-detail-dnr">
                <div class="cal-detail-hero" style="border-left-color: ${color};">
                    <div class="cal-detail-label">Asignatura</div>
                    <div class="cal-detail-subject">${Utils.sanitizeHTML(subject ? subject.name : 'Desconocido')}</div>
                </div>

                <div class="cal-detail-grid">
                    <div>
                        <div class="cal-detail-label">Formación</div>
                        <div class="cal-detail-value">${Utils.sanitizeHTML(formation ? formation.name : 'Desconocido')}</div>
                    </div>
                    <div>
                        <div class="cal-detail-label">Profesor</div>
                        <div class="cal-detail-value">${Utils.sanitizeHTML(professor ? `${professor.firstName} ${professor.lastName}` : 'Sin asignar')}</div>
                    </div>
                    <div>
                        <div class="cal-detail-label">Horario</div>
                        <div class="cal-detail-value">
                            <span class="cal-detail-day-pill">${Utils.sanitizeHTML(Utils.getDayName(classData.dayOfWeek))}</span>
                            ${classData.startTime} - ${classData.endTime} <span class="cal-detail-duration">(${duration.toFixed(1)}h)</span>
                        </div>
                    </div>
                    ${classData.classroom ? `
                        <div>
                            <div class="cal-detail-label">Aula</div>
                            <div class="cal-detail-value"><i class="fas fa-door-open" style="color: var(--dnr-teal-600); margin-right: 4px;"></i>${Utils.sanitizeHTML(classData.classroom)}</div>
                        </div>
                    ` : ''}
                </div>

                ${classData.professorId ? `
                    <div class="cal-detail-actions">
                        <button class="btn btn-secondary" style="width: 100%;" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();App.navigate("profesor/"+String(captured[0]));}, [classData.professorId])}>
                            <i class="fas fa-user"></i> Ver Perfil del Profesor
                        </button>
                    </div>
                ` : ''}
            </div>
        `;

        Utils.openModal('Detalles de la Clase', content);
    },

    /**
     * Show quick schedule form when clicking on calendar
     */
    showQuickScheduleForm(info, formationId = '') {
        const clickedDate = info.date || info.start;
        const clickedTime = clickedDate.toTimeString().slice(0, 5);
        const dayOfWeek = clickedDate.getDay();

        // Calculate end time (1 hour later)
        const startHour = parseInt(clickedTime.split(':')[0]);
        const endTime = `${String(startHour + 1).padStart(2, '0')}:00`;

        const formations = DataManager.getFormations();

        const content = `
            <form id="quick-schedule-form" ${UIEvents.attr("submit", function(event,captured){CalendarView.handleQuickSchedule(event);}, [])}>
                <div class="form-group">
                    <label class="form-label">Formación *</label>
                    <select class="form-select" id="quick-formation" required ${UIEvents.attr("change", function(event,captured){CalendarView.updateSubjectOptions();}, [])}>
                        <option value="">Seleccionar formación...</option>
                        ${formations.map(f => `
                            <option value="${Utils.sanitizeHTML(f.id)}">${Utils.sanitizeHTML(f.name)}</option>
                        `).join('')}
                    </select>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Asignatura *</label>
                    <select class="form-select" id="quick-subject" required disabled>
                        <option value="">Primero selecciona una formación</option>
                    </select>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Días de la semana *</label>
                    <div class="day-selector">
                        ${[1, 2, 3, 4, 5, 6, 0].map(day => `
                            <label class="day-checkbox">
                                <input type="checkbox" name="days" value="${day}" ${day === dayOfWeek ? 'checked' : ''}>
                                <span class="day-badge">${Utils.getDayName(day).charAt(0)}</span>
                            </label>
                        `).join('')}
                    </div>
                    <small style="color: var(--text-secondary); display: block; margin-top: 0.5rem;">
                        Selecciona uno o más días para programar la misma clase
                    </small>
                </div>
                
                <div class="grid grid-2">
                    <div class="form-group">
                        <label class="form-label">Hora inicio *</label>
                        <input type="time" step="900" class="form-input" id="quick-start-time" required value="${clickedTime}">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Hora fin *</label>
                        <input type="time" step="900" class="form-input" id="quick-end-time" required value="${endTime}">
                    </div>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Aula</label>
                    <input type="text" class="form-input" id="quick-classroom" placeholder="Ej: Aula 101">
                </div>
                
                <div class="grid grid-2">
                    <div class="form-group">
                        <label class="form-label">Fecha inicio *</label>
                        <input type="date" class="form-input" id="quick-start-date" 
                               value="${this.dateKey(clickedDate)}" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Fecha fin</label>
                        <input type="date" class="form-input" id="quick-end-date">
                    </div>
                </div>
                
                <div id="quick-conflicts" style="display: none;"></div>
                
                <div class="flex gap-1" style="margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-calendar-plus"></i> Programar
                    </button>
                </div>
            </form>
        `;

        Utils.openModal('Programación Rápida', content);
        const select = document.getElementById('quick-formation');
        if (formationId && select) { select.value = formationId; this.updateSubjectOptions(); }
    },

    /**
     * Update subject options based on selected formation
     */
    updateSubjectOptions() {
        const formationId = document.getElementById('quick-formation').value;
        const subjectSelect = document.getElementById('quick-subject');

        if (!formationId) {
            SafeHTML.set(subjectSelect, '<option value="">Primero selecciona una formación</option>');
            subjectSelect.disabled = true;
            return;
        }

        const subjects = DataManager.getSubjectsByFormation(formationId);

        if (subjects.length === 0) {
            SafeHTML.set(subjectSelect, '<option value="">No hay asignaturas en esta formación</option>');
            subjectSelect.disabled = true;
            return;
        }

        subjectSelect.disabled = false;
        SafeHTML.set(subjectSelect, '<option value="">Seleccionar asignatura...</option>' +
            subjects.map(s => {
                const professor = DataManager.getProfessor(s.professorId);
                const professorName = professor ? `${professor.firstName} ${professor.lastName}` : 'Sin profesor';
                return `<option value="${Utils.sanitizeHTML(s.id)}">${Utils.sanitizeHTML(s.name)} (${Utils.sanitizeHTML(professorName)})</option>`;
            }).join(''));
    },

    /**
     * Handle quick schedule form submission
     */
    handleQuickSchedule(event) {
        event.preventDefault();

        const formationId = document.getElementById('quick-formation').value;
        const subjectId = document.getElementById('quick-subject').value;
        const startTime = document.getElementById('quick-start-time').value;
        const endTime = document.getElementById('quick-end-time').value;
        const classroom = document.getElementById('quick-classroom').value;
        const startDate = document.getElementById('quick-start-date').value;
        const endDate = document.getElementById('quick-end-date').value;

        // Get selected days
        const selectedDays = Array.from(document.querySelectorAll('input[name="days"]:checked'))
            .map(cb => parseInt(cb.value));

        if (selectedDays.length === 0) {
            Utils.showToast('Debes seleccionar al menos un día', 'error');
            return;
        }

        // Validate times
        if (endTime <= startTime) {
            Utils.showToast('La hora de fin debe ser posterior a la hora de inicio', 'error');
            return;
        }

        const subject = DataManager.getSubject(subjectId);
        if (!subject) {
            Utils.showToast('Error: Asignatura no encontrada', 'error');
            return;
        }

        // Check conflicts for each day
        const conflicts = [];
        const success = [];

        selectedDays.forEach(dayOfWeek => {
            const conflict = DataManager.checkProfessorConflict(
                subject.professorId,
                dayOfWeek,
                startTime,
                endTime
            );

            if (conflict.conflict) {
                const conflictSubject = DataManager.getSubject(conflict.class.subjectId);
                const conflictFormation = DataManager.getFormation(conflict.class.formationId);
                conflicts.push({
                    day: dayOfWeek,
                    dayName: Utils.getDayName(dayOfWeek),
                    subjectName: conflictSubject ? conflictSubject.name : 'Desconocido',
                    formationName: conflictFormation ? conflictFormation.name : 'Desconocido',
                    time: `${conflict.class.startTime} - ${conflict.class.endTime}`
                });
            } else {
                success.push(dayOfWeek);
            }
        });

        // If there are conflicts, show warning
        if (conflicts.length > 0) {
            this.showConflictWarning(conflicts, success, {
                formationId,
                subjectId,
                professorId: subject.professorId,
                startTime,
                endTime,
                classroom,
                startDate,
                endDate
            });
            return;
        }

        // Create classes for all selected days
        let created = 0;
        selectedDays.forEach(dayOfWeek => {
            DataManager.createClass({
                subjectId: subjectId,
                formationId: formationId,
                professorId: subject.professorId,
                dayOfWeek: dayOfWeek,
                startTime: startTime,
                endTime: endTime,
                classroom: classroom,
                startDate: startDate,
                endDate: endDate || null
            });
            created++;
        });

        Utils.showToast(
            `${created} clase${created > 1 ? 's' : ''} programada${created > 1 ? 's' : ''} correctamente`,
            'success'
        );

        Utils.closeModal();

        this.refresh();
    },

    /**
     * Show conflict warning with option to proceed with non-conflicting days
     */
    showConflictWarning(conflicts, successDays, formData) {
        const conflictsDiv = document.getElementById('quick-conflicts');

        let html = '<div class="conflict-warning">';
        html += '<h4 style="margin-bottom: 0.75rem; color: var(--error-color);"><i class="fas fa-exclamation-triangle"></i> Conflictos Detectados</h4>';

        conflicts.forEach(c => {
            html += `
                <div class="conflict-item error">
                    <i class="fas fa-times-circle"></i>
                    <strong>${c.dayName}:</strong> Conflicto con "${c.subjectName}" (${Utils.sanitizeHTML(c.formationName)}) a las ${c.time}
                </div>
            `;
        });

        if (successDays.length > 0) {
            html += '<h4 style="margin-top: 1rem; margin-bottom: 0.75rem; color: var(--success-color);">Días Disponibles</h4>';
            successDays.forEach(day => {
                html += `
                    <div class="conflict-item success">
                        <i class="fas fa-check-circle"></i>
                        <strong>${Utils.getDayName(day)}:</strong> Disponible
                    </div>
                `;
            });

            html += `
                <div style="margin-top: 1rem;">
                    <button type="button" class="btn btn-primary" style="width: 100%;" 
                            ${UIEvents.attr("click", function(event,captured){CalendarView.createClassesForDays(captured[0],captured[1]);}, [JSON.stringify(successDays), JSON.stringify(formData).replace(/"/g, '&quot;')])}>
                        <i class="fas fa-calendar-check"></i> Programar solo en días disponibles (${successDays.length})
                    </button>
                </div>
            `;
        }

        html += '</div>';

        SafeHTML.set(conflictsDiv, html);
        conflictsDiv.style.display = 'block';
    },

    /**
     * Create classes only for specified days (after conflict resolution)
     */
    createClassesForDays(days, formData) {
        let created = 0;
        days.forEach(dayOfWeek => {
            DataManager.createClass({
                subjectId: formData.subjectId,
                formationId: formData.formationId,
                professorId: formData.professorId,
                dayOfWeek: dayOfWeek,
                startTime: formData.startTime,
                endTime: formData.endTime,
                classroom: formData.classroom,
                startDate: formData.startDate,
                endDate: formData.endDate || null
            });
            created++;
        });

        Utils.showToast(
            `${created} clase${created > 1 ? 's' : ''} programada${created > 1 ? 's' : ''} correctamente`,
            'success'
        );

        Utils.closeModal();

        this.refresh();
    }
};

// Make CalendarView globally available
window.CalendarView = CalendarView;
